import { useState } from "react";
import { AnimatedModal } from "../../../components/AnimatedModal";
import { CloseIcon } from "../../../components/CloseIcon";
import { H3 } from "../../../components/Heading";
import { Pills } from "../../../components/Pills";
import { SelectUserPill } from "../../../components/SelectUserPill";
import { TextArea } from "../../../components/tailwind/TextArea";
import { TextInput } from "../../../components/tailwind/TextInput";
import { UserPill } from "../../../components/UserPill";
import {
  Account,
  TaskPriority,
  useCreateTaskMutation,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { Button } from "../../../components/tailwind/Button";
import { getUnixTime } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { toasts } from "../../../components/toasts/toasts";
import { DatePicker } from "../../../components/DatePicker";
import { SelectTaskPriority } from "../../../components/SelectTaskPriorityPill";

export function NewDealTaskModal(props: {
  open: boolean;
  onClose: () => void;
  firmID: string;
  dealID: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();

  const createTask = useCreateTaskMutation(client);

  const [assignTo, setAssignTo] = useState<
    Pick<Account, "id" | "name" | "avatar" | "avatarColor">[] | null
  >(null);

  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [priority, setPriority] = useState<TaskPriority | null>(null);

  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");

  const [description, setDescription] = useState("");

  function clearForm() {
    setTitle("");
    setAssignTo(null);
    setDueDate(null);
    setDescription("");
    setTitleError("");
    setPriority(null);
  }

  return (
    <AnimatedModal
      size="xl"
      open={props.open}
      onClose={() => {
        clearForm();
        props.onClose();
      }}
    >
      <div className="flex items-center justify-between">
        <H3>New task</H3>
        <CloseIcon
          onClose={() => {
            clearForm();
            props.onClose();
          }}
        />
      </div>

      <form
        className="mt-5"
        onSubmit={(e) => {
          e.preventDefault();

          if (title === "") {
            setTitleError("Title is required");
            return;
          }

          createTask.mutate(
            {
              input: {
                title,
                description,
                assignedTo: assignTo ? assignTo.map((a) => a.id) : undefined,
                dueDate: dueDate ? getUnixTime(dueDate) : undefined,
                dealID: props.dealID,
                priority: priority ?? TaskPriority.Low,
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: ["Tasks"],
                });
                props.onClose();
                toasts.success("Task created");
                clearForm();
              },
            },
          );
        }}
      >
        <TextInput
          label="Title"
          placeholder="New task..."
          value={title}
          onChange={(e) => {
            setTitleError("");
            setTitle(e.currentTarget.value);
          }}
          error={titleError}
        />

        <p className="mt-3 block text-sm font-medium leading-6 text-gray-900">
          Description
        </p>
        <TextArea
          rows={5}
          name="Description"
          value={description}
          onChange={(e) => {
            setDescription(e.currentTarget.value);
          }}
          placeholder="Description of the task..."
        />

        <div className="flex mt-3 gap-x-4 items-center">
          <div className="">
            <Pills>
              {assignTo?.map((a) => (
                <UserPill
                  key={a.id}
                  account={a}
                  onRemove={() => {
                    setAssignTo(assignTo?.filter((at) => at.id !== a.id));
                  }}
                />
              ))}
              <SelectUserPill
                excludedAccountIds={assignTo?.map((a) => a.id ?? "")}
                onUserSelected={(acc) => {
                  setAssignTo([...(assignTo ?? []), acc as Account]);
                }}
              />
            </Pills>
          </div>

          <div className="">
            <Pills>
              <SelectTaskPriority
                onPriorityChanged={(p) => {
                  setPriority(p);
                }}
                priority={priority ?? undefined}
              />
            </Pills>
          </div>

          <div className="mt-2">
            <DatePicker
              value={dueDate ?? undefined}
              onChange={(e) => {
                setDueDate(e);
              }}
              pretext="Due"
              onRemove={() => {
                setDueDate(null);
              }}
            />
          </div>
        </div>

        <div className="mt-12 items-center justify-end flex">
          <Button
            isDisabled={title === ""}
            isLoading={createTask.isPending}
            variant="positive"
            text="Create task"
            loadingText="Creating..."
            type="submit"
          />
        </div>
      </form>
    </AnimatedModal>
  );
}
